import { useEffect, useState } from 'react';
import { content } from '@/api/endpoints';
import { pickList, type JsonMap } from '@/api/client';
import { Icon, type IconName } from '@/components/icons';
import { str } from '@/utils/format';
import { mediaUrl as resolveMediaUrl } from '@/utils/mediaUrl';

export function CustomerHero({
  placement,
  fallbackTitle,
  fallbackText,
  icon,
}: {
  placement: 'units' | 'games';
  fallbackTitle: string;
  fallbackText: string;
  icon: IconName;
}) {
  const [slides, setSlides] = useState<JsonMap[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await content.slides(placement);
      if (cancelled || !res.success) return;
      const list = pickList(res, 'slides', 'items', 'data').filter(
        (s) => Number(s.is_active ?? 1) !== 0 && !!str(s.image_url ?? s.image).trim(),
      );
      setSlides(list);
      setIndex(0);
    })();
    return () => {
      cancelled = true;
    };
  }, [placement]);

  useEffect(() => {
    if (slides.length < 2) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), 4300);
    return () => window.clearInterval(id);
  }, [slides.length]);

  const current = slides[index] ?? null;
  const imageUrl = resolveMediaUrl(current?.image_url ?? current?.image);
  const heroTone = placement === 'games' ? 'games-hero' : 'units-hero';

  if (imageUrl) {
    return (
      <section className={`customer-hero-card image-only-slider ${heroTone}`}>
        <img className="hero-slide-img" src={imageUrl} alt="" loading="eager" />
        {slides.length > 1 ? (
          <div className="hero-dots slider-dots">
            {slides.slice(0, 8).map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === index ? 'active' : ''}
                onClick={() => setIndex(i)}
                aria-label={`السلايد ${i + 1}`}
              />
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className={`customer-hero-card ${heroTone}`}>
      <div className="hero-copy">
        <h1>{fallbackTitle}</h1>
        <p>{fallbackText}</p>
        <div className="hero-dots">
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className={`hero-visual ${placement === 'games' ? 'game-visual' : 'unit-visual'}`}>
        <Icon name={icon} size={placement === 'games' ? 46 : 42} />
      </div>
    </section>
  );
}
